<template>
  <div class="questionnaire-prompt-container">
    <div class="questionnaire-prompt panel row">
      <div class="prompt-placement-buttons">
        <button @click="moveUp" title="Move prompt up" aria-label="Move prompt up" class="btn btn-outlined">
          <span class="fas fa-angle-up" aria-hidden="true"></span>
        </button>
        <button @click="moveDown" title="Move prompt down" aria-label="Move prompt down" class="btn btn-outlined">
          <span class="fas fa-angle-down" aria-hidden="true"></span>
        </button>
      </div>
      <div class="questionnaire-prompt-container">
        <div class="questionnaire-prompt-header">
          <h3>Question {{ number }}.</h3>
          <button title="Remove prompt" aria-label="Remove prompt" @click="removePrompt" class="btn btn-danger">
            <span class="fas fa-trash-alt" aria-hidden="true"></span>
          </button>
        </div>
        <hr>
        <div class="detail-form col-md-6 col-xs-12">
          <k-text-editor
            v-model="prompt"
            label="Question Text"
          ></k-text-editor>
        </div>
        <div v-if="responseOptions.length > 0" class="detail-form col-md-6 col-xs-12">
          <span>Response type</span>
          <k-dropdown :options="dropdownResponseOptions" v-model="responseTypeIndex" :showSearch="false"></k-dropdown>
        </div>
        <div v-if="reviewOptions.length > 0" class="detail-form col-md-6 col-xs-12">
          <span>Review type</span>
          <k-dropdown :options="dropdownReviewOptions" v-model="reviewTypeIndex" :showSearch="false"></k-dropdown>
        </div>
        <div v-if="isText && aiFeaturesSupported" class="detail-form col-md-6 col-xs-12 d-flex align-items-center">
          <div class="col-md-6">
            <span>Enable AI Feedback</span>
            <k-toggle v-model="enableAiFeedback" :class-id="`ai-${$.uid}`"></k-toggle>
          </div>
        </div>
        <div v-if="enableAiFeedback && isText" class="ai-feedback-container row">
          <div class="action-container col-md-12">
            <button class="btn btn-primary" @click="openFeedbackModal" :disabled="!testContextPrompt">
              Test Context Prompt
            </button>
          </div>
          <div class="detail-form column col-md-6">
            <k-text-editor
              v-model="aiFeedbackContextPrompt"
              label="AI Feedback Context Prompt"
              placeholder="Enter a context prompt that will help guide AI feedback or leave blank for default context prompt..."
            ></k-text-editor>
          </div>
          <div class="detail-form col-md-6">
            <k-text-editor
              v-model="currentResponse"
              label="Test Answer"
              placeholder="Enter a sample answer to test the AI feedback..."
            ></k-text-editor>
          </div>
        </div>
        <div v-if="isMultipleChoice" class="detail-form col-md-12">
          <span>Response choices</span>
          <answer-list v-model="choices" :setCorrect="false"></answer-list>
          <div class="detail-form">
            <span>Choice amount
              <k-tooltip
                :text="tooltipText"
                trigger-label="More information about choice amount"
              >
                <i class="fas fa-info-circle" aria-hidden="true"></i>
              </k-tooltip>
            </span>
            <k-number-choice v-model="choiceAmount" :valueMin="1" :valueMax="choices?.length"></k-number-choice>
          </div>
        </div>
        <div v-if="isNumeric" class="detail-form col-md-12">
          <p class="text-muted">The response will be a numeric input as below:</p>
          <k-numeric-input
              v-model="currentResponse"
              :readOnly="true"
              inputPlaceholder="Enter a number..."
          ></k-numeric-input>
        </div>
        <div v-if="isDate" class="detail-form col-md-12">
          <p class="text-muted">The response will be a date selection input.</p>
        </div>
        <div v-if="isYesNo" class="detail-form col-md-12">
          <p class="text-muted">The response will be a <b>Yes</b> or <b>No</b> selection input.</p>
        </div>

      </div>
    </div>
      <questionnaire-feedback-modal
        :showModal="showFeedbackModal"
        :prompt="prompt"
        :aiFeedbackContextPrompt="aiFeedbackContextPrompt"
        :currentResponse="currentResponse"
        :isFeedbackTest="isFeedbackTest"
        @close="closeFeedbackModal"
      />
  </div>
</template>

<style>
.detail-form > .k-number-choice {
  display: flex;
}
</style>

<style scoped>
.questionnaire-prompt.panel {
  padding: 15px;
  display: flexbox;
  display: flex;
  flex-wrap: nowrap;
}

.questionnaire-prompt-header {
  display: flexbox;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px 0 10px 15px;
}

.questionnaire-prompt-header + hr {
  margin: 15px;
  border-color: var(--kate-background-body-alpha);
}

.questionnaire-prompt-container {
  flex: 1;
}

.detail-form {
  margin: 15px 0;
  display: inline-block;
}

.ai-feedback-container .detail-form {
  margin: 0;
}

.prompt-placement-buttons {
  display: flexbox;
  display: flex;
  flex-wrap: nowrap;
  justify-content: center;
  flex-direction: column;
  gap: 15px;
  border-right: 1px solid var(--border-secondary);
  padding-right: 15px;
}

textarea {
  resize: vertical;
  min-height: 30px;
}

button i {
  margin-left: 5px;
}

.ai-feedback-container {
  width: 100%;
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  gap: 5px 15px;
}

.ai-feedback-container > .column {
  flex: 1;
}

.action-container {
  text-align: right;
}

@media (max-width: 991px) {
  .questionnaire-prompt.panel {
    display: block;
  }

  .prompt-placement-buttons {
    flex-direction: row;
    padding: 0;
    border: 0;
  }

  .questionnaire-prompt-container {
    display: flex;
    flex-direction: column;
  }

  .ai-feedback-container {
    flex-direction: column;
  }
}
</style>

<script>
import { KTooltip } from '@kate/components';
import KTextEditor from '@base-components/k-text-editor.vue';
import KDropdown from '@base-components/k-dropdown.vue';
import KNumberChoice from '@base-components/k-number-choice.vue';
import KToggle from '@base-components/forms/k-toggle.vue';
import KNumericInput from '@base-components/k-numeric-input.vue';
import AnswerList from '../../quizzes/quiz_creation/quiz-answer-list.vue';
import QuestionnaireFeedbackModal from '../../../../../feedback/components/questionnaire-feedback-modal.vue';

export default {
  components: {
    KDropdown,
    KTextEditor,
    KTooltip,
    KNumberChoice,
    AnswerList,
    KToggle,
    QuestionnaireFeedbackModal,
    KNumericInput,
  },

  props: {
    modelValue: {
      type: Object,
    },
    number: {
      type: Number,
    },
    responseOptions: {
      type: Array,
      default: () => [],
    },
    reviewOptions: {
      type: Array,
      default: () => [],
    },
    aiFeaturesSupported: {
      type: Boolean,
      default: true,
    },
  },

  data() {
    return {
      prompt: this.modelValue.prompt,
      responseTypeIndex: this.getResponseTypeIndex(this.modelValue.responseType) || 0,
      reviewTypeIndex: this.getReviewTypeIndex(this.modelValue.reviewType) || 0,
      choices: this.modelValue?.answers?.choices,
      choiceAmount: this.modelValue?.answers?.choice_amount || null,
      enableAiFeedback: this.modelValue?.enableAiFeedback || false,
      aiFeedbackContextPrompt: this.modelValue?.aiFeedbackContextPrompt || '',
      currentResponse: this.modelValue?.currentResponse || '',
      showFeedbackModal: false,
      isFeedbackTest: false,
    };
  },

  watch: {
    modelValue() {
      this.prompt = this.modelValue.prompt;
      this.responseTypeIndex = this.getResponseTypeIndex(this.modelValue.responseType);
      this.reviewTypeIndex = this.getReviewTypeIndex(this.modelValue.reviewType);
      this.choices = this.modelValue?.answers?.choices;
      this.choiceAmount = this.modelValue?.answers?.choice_amount;
      this.enableAiFeedback = this.modelValue?.enableAiFeedback;
      this.aiFeedbackContextPrompt = this.modelValue?.aiFeedbackContextPrompt;
      this.currentResponse = this.modelValue?.currentResponse;
    },
    questionnairePrompt() {
      this.$emit('update:modelValue', Object.assign(this.modelValue, this.questionnairePrompt));
    },
  },

  computed: {
    tooltipText() {
      return 'The number of choices that a learner is able to select. If you wish to allow learners to select all answers, set this to the maximum number of choices.';
    },
    responseType() {
      return this.responseOptions[this.responseTypeIndex]?.value;
    },
    isNumeric() {
      return ['BKSB_SCORES', 'OTJ_HOURS'].includes(this.responseType);
    },
    isMultipleChoice() {
      return this.modelValue.responseType === 'MULTIPLE_CHOICE';
    },
    isText() {
      return ['LONG_TEXT', 'SHORT_TEXT'].includes(this.responseType);
    },
    isDate() {
      return this.modelValue.responseType === 'DATE';
    },
    isYesNo() {
      return this.modelValue.responseType === 'YES_NO';
    },
    testContextPrompt() {
      return this.currentResponse?.trim() && this.prompt?.trim();
    },
    questionnairePrompt() {
      return {
        // Only these properties can be edited within the questionnaire prompt
        prompt: this.prompt,
        responseType: this.responseOptions[this.responseTypeIndex]?.value,
        reviewType: this.reviewOptions[this.reviewTypeIndex]?.value,
        answers: {
          choice_amount: this.choiceAmount,
          choices: this.choices?.map(x => String(x)),
        },
        enableAiFeedback: this.enableAiFeedback,
        aiFeedbackContextPrompt: this.aiFeedbackContextPrompt,
      };
    },
    dropdownResponseOptions() {
      return this.responseOptions.map((option, i) => ({
        name: option.name,
        id: i,
      }));
    },
    dropdownReviewOptions() {
      return this.reviewOptions.map((option, i) => ({
        name: option.name,
        id: i,
      }));
    },
  },

  methods: {
    openFeedbackModal() {
      this.showFeedbackModal = true;
      this.isFeedbackTest = true;
    },
    closeFeedbackModal() {
      this.showFeedbackModal = false;
      this.isFeedbackTest = false;
    },
    removePrompt() {
      this.$emit('remove');
    },
    moveUp() {
      this.$emit('move-up');
    },
    moveDown() {
      this.$emit('move-down');
    },
    getResponseTypeIndex(responseType) {
      if (this.responseOptions.map(opt => opt.value).includes(responseType)) {
        return this.responseOptions.map(opt => opt.value).indexOf(responseType);
      }
      return 0;
    },
    getReviewTypeIndex(reviewType) {
      if (this.reviewOptions.map(opt => opt.value).includes(reviewType)) {
        return this.reviewOptions.map(opt => opt.value).indexOf(reviewType);
      }
      return 0;
    },
  },
};
</script>
